import React, { useState, useEffect, useCallback } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, TextField, Snackbar,
  Alert as MuiAlert,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHandshake, faKey, faCopy,
  faLink, faBoxOpen, faSliders, faCircleCheck, faClock, faLayerGroup,
  faUserGroup, faChevronDown, faShareNodes,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useRole } from "../../contexts/RoleContext";
import { useDarkMode } from "../../contexts/ThemeContext";
import { usePartnershipsData } from "../../hooks/usePartnershipsData";
import { useMaterialTypes } from "../../hooks/useMaterialTypes";
import { usePermissionGuard } from "../../hooks/usePermissionGuard";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import Card from "../../components/ui/Card";
import ActionButton from "../../components/ui/ActionButton";
import PartnershipActionModal from "../../components/Modals/PartnershipActionModal";
import PartnershipDetailPanel from "../../components/Partnerships/PartnershipDetailPanel";
import {
  generatePartnerKey, claimPartnerKey, pausePartnership,
  resumePartnership, revokePartnership, restorePartnership,
} from "../../api/partnershipsApi";
import "../../style/Card.css";
import "../../style/Partnerships.css";

const STATUS_COLORS = {
  pending: { bg: "rgba(255,167,38,0.12)", color: "#ffa726" },
  active: { bg: "rgba(102,187,106,0.12)", color: "#66bb6a" },
  paused: { bg: "rgba(158,158,158,0.12)", color: "#9e9e9e" },
  revoked: { bg: "rgba(239,83,80,0.12)", color: "#ef5350" },
  pending_approval: { bg: "rgba(171,130,255,0.12)", color: "#ab82ff" },
  expired: { bg: "rgba(120,120,120,0.12)", color: "#888888" },
};

const StatusChip = ({ status }) => {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <Chip label={status?.replace("_", " ")} size="small"
      sx={{ backgroundColor: s.bg, color: s.color, fontWeight: 600, fontSize: "0.7rem", textTransform: "capitalize", height: 22, letterSpacing: "0.3px" }} />
  );
};

function Partnerships() {
  const { t } = useTranslation();
  const { currentRole } = useRole();
  const { darkMode } = useDarkMode();
  const { hasPermission, isChecking } = usePermissionGuard("shared", "partnerships");
  const { donorData, receiverData, loading, error, refresh } = usePartnershipsData();
  const { materialTypes } = useMaterialTypes();

  const [isVisible, setIsVisible] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const showSnack = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  const [genLoading, setGenLoading] = useState(false);
  const [generatedKey, setGeneratedKey] = useState("");
  const [claimInput, setClaimInput] = useState("");
  const [claimLoading, setClaimLoading] = useState(false);

  // slide-in panel
  const [selectedPartnership, setSelectedPartnership] = useState(null);
  const [selectedSide, setSelectedSide] = useState(null);
  const [panelClosing, setPanelClosing] = useState(false);

  // partnership action modal
  const [actionModal, setActionModal] = useState({ open: false, action: null, id: null, partnerName: "" });
  const [actionProcessing, setActionProcessing] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showDonorHow, setShowDonorHow] = useState(false);

  const categories = [...new Set(materialTypes.map((m) => m.material_category).filter(Boolean))].sort();

  useEffect(() => {
    if (!loading) {
      setIsVisible(false);
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  useEffect(() => {
    setIsVisible(false);
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, [currentRole]);

  const handleGenerate = useCallback(async () => {
    setGenLoading(true);
    try {
      const res = await generatePartnerKey({});
      setGeneratedKey(res.partnership?.partner_key || "");
      showSnack(t("Partner key generated"));
      refresh();
    } catch (err) { showSnack(err.message, "error"); }
    finally { setGenLoading(false); }
  }, [refresh, t]);

  const handleCopy = (key) => { navigator.clipboard.writeText(key); showSnack(t("Copied to clipboard")); };

  const handleClaim = useCallback(async () => {
    if (!claimInput.trim()) return;
    setClaimLoading(true);
    try {
      await claimPartnerKey(claimInput.trim());
      showSnack(t("Partnership claimed! Click the row to configure your material preferences."));
      setClaimInput("");
      refresh();
    } catch (err) { showSnack(err.message, "error"); }
    finally { setClaimLoading(false); }
  }, [claimInput, refresh, t]);

  const openPanel = (partnership, side) => {
    setSelectedPartnership(partnership);
    setSelectedSide(side);
  };

  const closePanel = () => {
    setPanelClosing(true);
    setTimeout(() => {
      setSelectedPartnership(null);
      setSelectedSide(null);
      setPanelClosing(false);
    }, 560);
  };

  const openActionModal = useCallback((id, action, partnerName = "") => {
    setActionModal({ open: true, action, id, partnerName });
  }, []);

  const handleActionConfirm = useCallback(async (notes) => {
    const { id, action } = actionModal;
    setActionProcessing(true);
    try {
      if (action === "pause") await pausePartnership(id, notes);
      else if (action === "resume") await resumePartnership(id, notes);
      else if (action === "revoke") await revokePartnership(id, notes);
      else if (action === "restore") await restorePartnership(id, notes);
      showSnack(t(`Partnership ${action}d`));
      setActionModal({ open: false, action: null, id: null, partnerName: "" });
      closePanel();
      refresh();
    } catch (err) { showSnack(err.message, "error"); }
    finally { setActionProcessing(false); }
  }, [actionModal, refresh, t]);

  if (loading || isChecking || !hasPermission) return <LoadingSpinner fullPage message={t("Loading partnerships...")} />;

  const activeCount = currentRole === "donor" ? donorData.filter((p) => p.status === "active").length : receiverData.filter((p) => p.status === "active").length;
  const pendingCount = currentRole === "donor"
    ? donorData.filter((p) => p.status === "pending").length
    : receiverData.filter((p) => p.status === "paused").length;
  const totalCount = currentRole === "donor" ? donorData.length : receiverData.length;

  const donorCounters = [
    { icon: faCircleCheck, value: activeCount, title: t("Active"), description: t("Connected partners"), colorClass: "counter-green" },
    { icon: faClock, value: pendingCount, title: t("Pending"), description: t("Unclaimed keys"), colorClass: "counter-amber" },
    { icon: faLayerGroup, value: totalCount, title: t("Total Keys"), description: t("All generated keys"), colorClass: "counter-blue" },
  ];
  const receiverCounters = [
    { icon: faCircleCheck, value: activeCount, title: t("Active"), description: t("Receiving materials"), colorClass: "counter-blue" },
    { icon: faClock, value: pendingCount, title: t("Paused"), description: t("Temporarily on hold"), colorClass: "counter-amber" },
    { icon: faUserGroup, value: totalCount, title: t("Total"), description: t("All partnerships"), colorClass: "counter-purple" },
  ];
  const counters = currentRole === "donor" ? donorCounters : receiverCounters;

  const panelOpen = !!selectedPartnership || panelClosing;

  // ================================================================
  // MAIN RENDER
  // ================================================================
  return (
    <div className={`partnerships-container ${isVisible ? "visible" : ""}`} key={currentRole}>
      {error && <div className="partnerships-error-wrap"><MuiAlert severity="error" sx={{ borderRadius: "8px" }}>{error}</MuiAlert></div>}

      <div className="partnerships-counters-wrapper">
        <div className="counter-cards-container">
          {counters.map((card, index) => (
            <Card key={index} icon={card.icon} value={card.value} title={card.title} description={card.description} variant="counter" colorClass={card.colorClass} darkMode={darkMode} />
          ))}
        </div>
      </div>

      {/* DONOR VIEW */}
      {currentRole === "donor" && (
        <>
          <div className="partnerships-table-section" style={{ marginBottom: panelOpen ? 0 : undefined }}>
            <div className="partnerships-table-title-row">
              <h2 className="partnerships-table-title">{t("Your Partner Keys")} <span className="count-badge">({donorData.length})</span></h2>
              <ActionButton variant="secondary" icon={faKey} onClick={handleGenerate} loading={genLoading}>{t("Generate Key")}</ActionButton>
              <button className="partnerships-how-toggle" onClick={() => setShowDonorHow((v) => !v)}>
                {t("How does it work?")}
                <FontAwesomeIcon icon={faChevronDown} className={`how-toggle-chevron${showDonorHow ? " open" : ""}`} />
              </button>
            </div>
            {generatedKey && (
              <div className="partnerships-key-result donor-key-result">
                <span className="partnerships-key-value">{generatedKey}</span>
                <span className="partnerships-key-hint">{t("Expires in 72h")}</span>
                <ActionButton variant="default" iconOnly icon={faCopy} tooltip={t("Copy to clipboard")} onClick={() => handleCopy(generatedKey)} flashOnClick />
              </div>
            )}
          </div>

          <div className={`partnerships-how-collapse${showDonorHow ? " expanded" : ""}`}>
            <div className="partnerships-how-it-works">
              <div className="how-step"><div className="how-step-num">1</div><div className="how-step-content"><FontAwesomeIcon icon={faKey} className="how-step-icon" /><div><div className="how-step-title">{t("Generate a key")}</div><div className="how-step-desc">{t("Create a unique partnership key")}</div></div></div></div>
              <div className="how-connector" />
              <div className="how-step"><div className="how-step-num">2</div><div className="how-step-content"><FontAwesomeIcon icon={faShareNodes} className="how-step-icon" /><div><div className="how-step-title">{t("Share with receiver")}</div><div className="how-step-desc">{t("Send the key to your partner")}</div></div></div></div>
              <div className="how-connector" />
              <div className="how-step"><div className="how-step-num">3</div><div className="how-step-content"><FontAwesomeIcon icon={faHandshake} className="how-step-icon" /><div><div className="how-step-title">{t("Partnership active")}</div><div className="how-step-desc">{t("Materials are allocated automatically")}</div></div></div></div>
            </div>
          </div>

          <div className="partnerships-master-detail">
            <div className="partnerships-table-section" style={{ marginTop: 0 }}>
              {donorData.length === 0 ? (
                <div className="partnerships-empty">
                  <div className="partnerships-empty-icon"><FontAwesomeIcon icon={faKey} /></div>
                  <h3 className="partnerships-empty-title">{t("No partner keys yet")}</h3>
                  <p className="partnerships-empty-text">{t("Generate your first key to start connecting with receivers.")}</p>
                </div>
              ) : (
                <TableContainer className="partnerships-table-wrap">
                  <Table size="small">
                    <TableHead><TableRow>
                      <TableCell>{t("Key")}</TableCell>
                      <TableCell>{t("Receiver")}</TableCell>
                      <TableCell>{t("Status")}</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {donorData.map((p) => (
                        <TableRow
                          key={p.id}
                          className={`partnership-data-row${selectedPartnership?.id === p.id ? " row-selected" : ""}${!p.receiver_user_id ? " row-unclaimed" : ""}`}
                          onClick={() => openPanel(p, "donor")}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell>
                            <span className="partnership-key-mono">{p.partner_key?.slice(0, 12)}...</span>
                            <span onClick={(e) => e.stopPropagation()}>
                              <ActionButton variant="default" iconOnly icon={faCopy} tooltip={t("Copy")} onClick={() => handleCopy(p.partner_key)} flashOnClick style={{ marginLeft: 4 }} />
                            </span>
                          </TableCell>
                          <TableCell>{p.receiver_username ? (<div><div className="partnership-name">{p.receiver_username}</div><div className="partnership-org">{p.receiver_org_name}</div></div>) : (<span className="partnership-unclaimed">{t("Unclaimed")}</span>)}</TableCell>
                          <TableCell><StatusChip status={p.status} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </div>

            {panelOpen && selectedSide === "donor" && (
              <PartnershipDetailPanel
                key={selectedPartnership?.id}
                partnership={selectedPartnership}
                side="donor"
                closing={panelClosing}
                onClose={closePanel}
                onAction={(id, action, name) => openActionModal(id, action, name)}
                onCopyKey={handleCopy}
                onSnack={showSnack}
                categories={categories}
                materialTypes={materialTypes}
              />
            )}
          </div>
        </>
      )}

      {/* RECEIVER VIEW */}
      {currentRole === "receiver" && (
        <>
          {/* compact claim row */}
          <div className="partnerships-claim-row">
            <label className="partnerships-claim-label">{t("Claim Partner Key")}</label>
            <div className="partnerships-claim-input-group">
              <TextField size="small" placeholder="VRT-XXXXXXXXXXXXXXXXXXXX" value={claimInput} onChange={(e) => setClaimInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleClaim()}
                sx={{ width: 340, "& .MuiOutlinedInput-root": { fontFamily: "'JetBrains Mono', monospace", fontSize: "0.82rem", borderRadius: "8px", letterSpacing: "0.5px", height: 36 } }} />
              <ActionButton variant="primary" icon={faLink} onClick={handleClaim} loading={claimLoading} disabled={!claimInput.trim()}>{t("Claim")}</ActionButton>
            </div>
            <button className="partnerships-how-toggle" onClick={() => setShowHowItWorks((v) => !v)}>
              {t("How does it work?")}
              <FontAwesomeIcon icon={faChevronDown} className={`how-toggle-chevron${showHowItWorks ? " open" : ""}`} />
            </button>
          </div>

          {/* collapsible how-it-works */}
          <div className={`partnerships-how-collapse${showHowItWorks ? " expanded" : ""}`}>
            <div className="partnerships-how-it-works">
              <div className="how-step"><div className="how-step-num">1</div><div className="how-step-content"><FontAwesomeIcon icon={faKey} className="how-step-icon" /><div><div className="how-step-title">{t("Claim a key")}</div><div className="how-step-desc">{t("Get a key from your donor partner")}</div></div></div></div>
              <div className="how-connector" />
              <div className="how-step"><div className="how-step-num">2</div><div className="how-step-content"><FontAwesomeIcon icon={faSliders} className="how-step-icon" /><div><div className="how-step-title">{t("Configure materials")}</div><div className="how-step-desc">{t("Set categories and quantity filters")}</div></div></div></div>
              <div className="how-connector" />
              <div className="how-step"><div className="how-step-num">3</div><div className="how-step-content"><FontAwesomeIcon icon={faBoxOpen} className="how-step-icon" /><div><div className="how-step-title">{t("Auto-Requested")}</div><div className="how-step-desc">{t("Listings are requested automatically")}</div></div></div></div>
            </div>
          </div>

          <div className="partnerships-table-section">
            <h2 className="partnerships-table-title">{t("Your Partnerships")} <span className="count-badge">({receiverData.length})</span></h2>
          </div>

          <div className="partnerships-master-detail">
            <div className="partnerships-table-section" style={{ marginTop: 0 }}>
              {receiverData.length === 0 ? (
                <div className="partnerships-empty">
                  <div className="partnerships-empty-icon"><FontAwesomeIcon icon={faHandshake} /></div>
                  <h3 className="partnerships-empty-title">{t("No partnerships yet")}</h3>
                  <p className="partnerships-empty-text">{t("Claim a partner key above to start receiving materials automatically.")}</p>
                </div>
              ) : (
                <TableContainer className="partnerships-table-wrap">
                  <Table size="small">
                    <TableHead><TableRow>
                      <TableCell>{t("Donor")}</TableCell>
                      <TableCell>{t("Location")}</TableCell>
                      <TableCell>{t("Status")}</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {receiverData.map((p) => (
                        <TableRow
                          key={p.id}
                          className={`partnership-data-row${selectedPartnership?.id === p.id ? " row-selected" : ""}`}
                          onClick={() => openPanel(p, "receiver")}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell>
                            <div>
                              <div className="partnership-name">{p.donor_username}</div>
                              <div className="partnership-org">{p.donor_org_name}</div>
                            </div>
                          </TableCell>
                          <TableCell><span className="partnership-location">{p.donor_city || "—"}</span></TableCell>
                          <TableCell><StatusChip status={p.status} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </div>

            {panelOpen && selectedSide === "receiver" && (
              <PartnershipDetailPanel
                key={selectedPartnership?.id}
                partnership={selectedPartnership}
                side="receiver"
                closing={panelClosing}
                onClose={closePanel}
                onAction={(id, action, name) => openActionModal(id, action, name)}
                onCopyKey={handleCopy}
                onSnack={showSnack}
                categories={categories}
                materialTypes={materialTypes}
              />
            )}
          </div>
        </>
      )}

      <PartnershipActionModal
        open={actionModal.open}
        onClose={() => setActionModal({ open: false, action: null, id: null, partnerName: "" })}
        onConfirm={handleActionConfirm}
        action={actionModal.action || "pause"}
        partnerName={actionModal.partnerName}
        darkMode={darkMode}
        isProcessing={actionProcessing}
      />
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <MuiAlert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled" sx={{ width: "100%", borderRadius: "8px" }}>{snackbar.message}</MuiAlert>
      </Snackbar>
    </div>
  );
}

export default Partnerships;
